/**
 * Rating tiers + band colours — what counts as Excellent/Fair/Needs work,
 * and the green/amber/red palette BOTH clients render.
 *
 * Add a tier by adding a row (e.g. { band: 'amber', minPct: 75,
 * label: 'Good' }); keep rows sorted by minPct descending and keep a
 * minPct-0 row last. Labels are user-facing copy.
 */

export type RatingBand = 'green' | 'amber' | 'red'

export interface RatingTier {
  band: RatingBand
  /** Tier applies at/above this %; the last tier must have minPct 0. */
  minPct: number
  /** User-facing label rendered by both clients. */
  label: string
}

/** Session headline + zone headlines (green from 90 — stricter than rows;
 * one lap off a 4-lap row is still a green habit, but a whole zone only
 * reads "solid" from 90 up — pass 12). */
export const ZONE_RATING_TIERS: RatingTier[] = [
  { band: 'green', minPct: 90, label: 'Excellent' },
  { band: 'amber', minPct: 60, label: 'Fair' },
  { band: 'red', minPct: 0, label: 'Needs work' },
]

/** Row-level metrics (brake point, brake release, entry/exit speed, speed
 * curve, time) — green from 80. */
export const ROW_RATING_TIERS: RatingTier[] = [
  { band: 'green', minPct: 80, label: 'Excellent' },
  { band: 'amber', minPct: 60, label: 'Fair' },
  { band: 'red', minPct: 0, label: 'Needs work' },
]

/** First tier whose minPct the percentage reaches; tables are sorted
 * descending so this is the tightest matching tier. */
export function ratingTierFor(pct: number, tiers: RatingTier[]): RatingTier {
  return tiers.find((t) => pct >= t.minPct) ?? tiers[tiers.length - 1]
}

export interface BandColor {
  /** Bars, dots, map lines, gradients (500-series). */
  solid: string
  /** Chip/badge TEXT on a tinted background (600-series, darker). Chip
   * backgrounds are ALWAYS chipText + '22' alpha — one rule, no extra hexes. */
  chipText: string
}

/** The one green/amber/red palette both clients consume. */
export const BAND_COLORS: Record<RatingBand, BandColor> = {
  green: { solid: '#10B981', chipText: '#059669' },
  amber: { solid: '#F59E0B', chipText: '#D97706' },
  red: { solid: '#EF4444', chipText: '#DC2626' },
}

const tierMin = (tiers: RatingTier[], band: RatingBand): number => {
  const tier = tiers.find((t) => t.band === band)
  if (!tier) throw new Error(`no ${band} tier`)
  return tier.minPct
}

/** Derived compat exports — the tier tables are the source of truth. */
export const PCT_GREEN_MIN = tierMin(ROW_RATING_TIERS, 'green')
export const PCT_AMBER_MIN = tierMin(ROW_RATING_TIERS, 'amber')
export const ZONE_PCT_GREEN_MIN = tierMin(ZONE_RATING_TIERS, 'green')
